import { DatePipe, DOCUMENT, NgSwitch, NgSwitchCase, NgIf, NgClass } from '@angular/common';
import { Component, Inject, OnInit, ViewChild, DestroyRef, inject } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { LoginForm } from '../../../core/models/index';
import { LOGO } from '../../../core/models/logo.enum';
import { AlertService } from '../../../core/services/alert.service';
import { LoginService } from '../../../core/services/login.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { UtilsService } from '../../../core/services/utils';
import { MaintenanceWindowNotice } from '../../../portal/models/maintenanceWindowNotice';
import { LoginResponseType } from '../../constant/login-response-type';
import { LoginResponse } from '../../models/login/LoginResponse';
import { AuthRoutes } from '../../models/routes/AuthRoutes.constant';
import { GtOAuthService } from 'src/app/core/services/gt-oauth.service';
import { GtSessionStorageService } from 'src/app/core/services/gt-session-storage.service';
import { GtLocalStorageService } from '../../../core/services/gt-local-storage.service';
import { LoginPageResponse } from '../../models/headless-cms/login-page-response';
import { OAuthConstants } from '../../constant/oAuth.constants';
import * as moment from 'moment';
import { LoginChallengeDataService } from '../../../core/services/login-challenge-data.service';
import { DomainDataService } from '../../../core/services/domain-data.service';
import { LoginPageApiService } from '../../../core/services/login-page-api.service';
import { encrypt } from '../../../utils/encrypt-util';
import { LinkedinSigninComponent } from '../linkedin-siginin/linkedin-signin.component';
import { FacebookSigininComponent } from '../facebook-signin/facebook-siginin.component';
import { GoogleSigninComponent } from '../google-signin/google-signin.component';
import { GtAutofocusDirective } from '../../../core/directives/gt-autofocus.directive';
import { ErrorContainerComponent } from '../error-container/error-container.component';
import { GtAppLoaderComponent } from '../../../core/components/gt-app-loader/gt-app-loader.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    providers: [DatePipe],
    imports: [
        NgSwitch,
        NgSwitchCase,
        GtAppLoaderComponent,
        NgIf,
        FormsModule,
        ErrorContainerComponent,
        GtAutofocusDirective,
        NgClass,
        RouterLink,
        GoogleSigninComponent,
        FacebookSigininComponent,
        LinkedinSigninComponent,
    ]
})
export class LoginComponent implements OnInit {
  public loginForm: LoginForm;
  public loginConstant = LoginResponseType;
  public loginResponse: LoginResponse;
  public oAuthPath = AuthRoutes;
  public challenge: string;
  public userLoginField = "Employee No";
  public failToInitalteLogin = false;
  public isLoading = true;
  public isSubmitted = true;
  public errorMessage: string;
  public clientLogo = LOGO.CLIENT;
  public disableOpenIdLogin = true;
  public disableSamlLoginBtn = true;
  public samlProvider = "";
  public accountDisabled = false;
  public domainName = "";
  public viewState = "";
  public errorState = "";
  public readonly disabled = "disabled";
  public readonly loading = "loading";
  public readonly invalidLogin = "invalidLogin";
  public readonly emoji = "&#128075;";
  public showPassword = false;
  public isDisableLogin = false;
  @ViewChild("login")
  private formGroup: NgForm;
  private loginPageResponse: LoginPageResponse;
  private destroyRef = inject(DestroyRef);
  constructor(
    private route: ActivatedRoute,
    private _router: Router,
    private gtOauthService: GtOAuthService,
    private loginService: LoginService,
    private alertService: AlertService,
    private gtSessionService: GtSessionStorageService,
    @Inject(DOCUMENT) private document: any,
    private _sessionStorage: SessionStorageService,
    private utilsService: UtilsService,
    private datePipe: DatePipe,
    private _localStorage: GtLocalStorageService,
    private _loginChallengeDataService: LoginChallengeDataService,
    private _domainDataService: DomainDataService,
    private _loginPageApiService: LoginPageApiService
  ) {
    this.loginSubscriber = this.loginSubscriber.bind(this);
    this.initateLoginSubscriber = this.initateLoginSubscriber.bind(this);
    this.initiateLoginErrorSubscriber = this.initiateLoginErrorSubscriber.bind(
      this
    );
    this.loginErrSubscriber = this.loginErrSubscriber.bind(this);
  }

  public ngOnInit() {
    this.loginForm = <LoginForm>{};
    this.domainName = this._domainDataService.getDomain();
    this._domainDataService.domainSource
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this._setDomainName.bind(this));
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params: Params) => {
        this.challenge = params.login_challenge;
      });
    if (this.challenge) {
      this.isLoading = true;
      this._changeViewState(this.loading);
      this.loginService
        .initiateLogin(this.challenge)
        .pipe(
          finalize(() => (this.isLoading = false)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(
          this.initateLoginSubscriber,
          this.initiateLoginErrorSubscriber
        );
      this._sessionStorage.put("nonce", this._localStorage.value("nonce"));
      this._loginChallengeDataService.setLoginChallenge(this.challenge);
      this.loadLoginPageRes();
    }
  }

  public submit() {
    this.loginResponse.message = null;
    this.errorMessage = null;
    if (this.formGroup.invalid) {
      this.isSubmitted = false;
      this._changeErrorState(this.invalidLogin);
      return;
    }
    if (this.loginChallengeExpired()) {
      this._router.navigate([AuthRoutes.CHALLENGE_EXPIRY_ROUTE]);
      return;
    }
    const formData = { ...this.loginForm };

    const encPassword = encrypt(formData.password);
    formData.password = encPassword;
    this.isSubmitted = true;
    this.isDisableLogin = true;
    this.loginService
      .login(formData, this.challenge)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.loginSubscriber, this.loginErrSubscriber);
  }

  public onUsernameChange(event) {
    this.loginForm.username = event;
    this.isSubmitted = false;
  }

  public onPasswordChange(event) {
    this.loginForm.password = event;
    this.isSubmitted = false;
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public loadLoginPageRes() {
    this._loginPageApiService
      .getLoginPage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.loginPageResponse = res;
        const { disableOpenIdAuthLogin, disableSamlLogin, samlProvider } = this.loginPageResponse;
        this.userLoginField =
          this.loginPageResponse?.employeeLoginField || this.userLoginField;
        this.disableOpenIdLogin = disableOpenIdAuthLogin;
        this.disableSamlLoginBtn = disableSamlLogin;
        this.samlProvider = samlProvider;
      });
  }

  public samlLogin() {
    this.document.location.href = this.loginPageResponse.samlUrl;
  }
  private loginSubscriber(res: LoginResponse) {
    this.loginResponse = res;
    const isMFAEnabled = (
      this.loginResponse.redirectUrl?.trim() === AuthRoutes.TWO_FACTOR_LOGIN.trim()
    );
    this._changeViewState(this.loginResponse.loginType);
    if (this.loginService.isRedirect(res)) {
       if(isMFAEnabled) {
        return this.navigateLogin2FA(res);
       }
      this.document.location.href = this.loginResponse.redirectUrl;
    }
    this.resetLoginForm();
  }

  private loginErrSubscriber(err) {
    if (!err) {
      return;
    }
    const { error } = err;
    this.errorMessage = error.message;
    this.isDisableLogin = false;
    this.changeErrorViewState(error);
  }

  private resetLoginForm() {
    if (!this.utilsService.isEmpty(this.loginResponse.message)) {
      this.loginForm.password = "";
      this.isDisableLogin = false;
    }
  }

  private initateLoginSubscriber(res: LoginResponse) {
    this.loginResponse = res;
    if (this.loginResponse.enabled) {
      this._changeViewState(this.loginResponse.loginType);
    } else {
      this.accountDisabled = true;
      this._changeViewState(this.disabled);
      return;
    }
    if (this.loginService.isRedirect(res)) {
      this.document.location.href = this.loginResponse.redirectUrl;
    }

    if (this.loginResponse.maintenanceAnnounced) {
      this.loginService
        .getMaintenanceWindowNotes(this.challenge, "announced")
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((notes: MaintenanceWindowNotice) => {
          this.showMaintenanceMessage(notes);
        });
    }
  }

  private initiateLoginErrorSubscriber(error) {
    this.failToInitalteLogin = true;
    console.error("error occurred while initiate login ", error.message);
    const errorMessage = { message: "Initiate login request failed" };
    this.alertService.setMessage(errorMessage);
    this.gtOauthService.forceFullyInitiateOauthFlow();
  }

  private showMaintenanceMessage({ windows }) {
    if (this.utilsService.isEmpty(windows)) {
      return;
    }
    let [notice] = windows;
    let { start_time, end_time } = notice;
    start_time = this.datePipe.transform(start_time, "d MMM yyyy HH:mm a");
    end_time = this.datePipe.transform(start_time, "d MMM yyyy HH:mm a");
    notice = {
      message: `Site will not be available from ${start_time} to ${end_time} due to a scheduled maintenance.`,
    };
    this.alertService.setMessage(notice);
  }

  private loginChallengeExpired(): boolean {
    const loginInitiateTime = parseFloat(
      this.gtSessionService.value(OAuthConstants.LOGIN_INITIATE_TIME)
    );
    const challengeExpireDuration = parseFloat(
      this.gtSessionService.value(
        OAuthConstants.LOGIN_CHALLENGE_EXPIRY_DURATION
      )
    );
    const currentTime = moment.now();
    const challengeExpired =
      moment.duration(currentTime - loginInitiateTime).asMinutes() >=
      challengeExpireDuration;
    return challengeExpired;
  }
  private _setDomainName(value) {
    this.domainName = value;
  }
  private _changeViewState(value) {
    this.viewState = value;
  }
  private _changeErrorState(value) {
    this.errorState = value;
  }

  private changeErrorViewState(error) {
    let errorState: string = error.errorState;
    this._changeErrorState(errorState);
    console.error('on login error!', error);
  }

  private navigateLogin2FA({message, otpType}: any): void {
    this._router.navigate([AuthRoutes.TWO_FACTOR_LOGIN], {
      state: {
        otpsession: message,
        otpType: otpType
      }
    });
    }

}
